import { Injectable } from "@angular/core";
import { Material, WayOfMeasurement } from "@partikelart/particles/domain";
import { SpecificationRepository } from "@partikelart/specification/data-access-core";
import {
	SizeClassLimitNormalisation,
	type Specification,
	sizeClassKey,
	VDA19_SIZE_CLASSES,
} from "@partikelart/specification/domain";
import type { CreateSpecificationDto, UpdateSpecificationDto } from "@partikelart/specification/dtos";
import { Observable, of, throwError } from "rxjs";
import { delay } from "rxjs/operators";

const SITE_1 = "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a";
const SITE_2 = "e1f2a3b4-c5d6-4e7f-8a9b-0c1d2e3f4a5b";

const PART_1 = "00000000-0000-4000-8000-000000000001";
const PART_2 = "00000000-0000-4000-8000-000000000002";
const PART_3 = "00000000-0000-4000-8000-000000000003";
const PART_4 = "00000000-0000-4000-8000-000000000004";

@Injectable({
	providedIn: "root",
})
export class DemoSpecificationRepository implements SpecificationRepository {
	private readonly latencyMs = 200;
	private specifications: Specification[] = [
		{
			id: "a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d",
			siteId: SITE_1,
			name: "Demo Spec Alpha (Site 1)",
			partNumber: "ART-001",
			partId: PART_1,
			surfaceAreaCm2: 250,
			numberOfParts: 5,
			pdfBlobPath: null,
			active: true,
			sizeClassLimitNormalisation: SizeClassLimitNormalisation.PerPart,
			createdAt: new Date("2026-01-10T12:00:00Z"),
			updatedAt: new Date("2026-01-10T12:00:00Z"),
			extractionSteps: [],
			sizeClassLimits: VDA19_SIZE_CLASSES.map((sc) => ({
				id: `scl-alpha-${sc.sizeClassLetterKey ?? sizeClassKey(sc)}`,
				specificationId: "a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d",
				sizeClassKey: sizeClassKey(sc),
				lower: sc.lower,
				upper: sc.upper ?? null,
				label: sc.label,
				maxCount: 1000,
				material: Material.METAL,
				useTotal: true,
				wayOfMeasurement: WayOfMeasurement.FeretMax,
			})),
			categoryLimits: [
				{
					id: "cat-alpha-1",
					specificationId: "a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d",
					category: "metal",
					maxSizeUm: 10,
					wayOfMeasurement: WayOfMeasurement.FeretMax,
				},
				{
					id: "cat-alpha-2",
					specificationId: "a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d",
					category: "non_metal",
					maxSizeUm: 500,
					wayOfMeasurement: WayOfMeasurement.FeretMax,
				},
				{
					id: "cat-alpha-3",
					specificationId: "a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d",
					category: "fiber",
					maxSizeUm: 1000,
					wayOfMeasurement: WayOfMeasurement.FeretMax,
				},
			],
		},
		{
			id: "c3d4e5f6-a7b8-4c9d-0e1f-2a3b4c5d6e7f",
			siteId: SITE_1,
			name: "Demo Spec Beta (Site 1)",
			partNumber: "ART-002",
			partId: PART_2,
			surfaceAreaCm2: 300,
			numberOfParts: null,
			pdfBlobPath: null,
			active: true,
			sizeClassLimitNormalisation: SizeClassLimitNormalisation.Per1000SqCm,
			createdAt: new Date("2026-02-01T08:00:00Z"),
			updatedAt: new Date("2026-02-01T08:00:00Z"),
			extractionSteps: [],
			sizeClassLimits: [
				{
					id: "beta-scl-1",
					specificationId: "c3d4e5f6-a7b8-4c9d-0e1f-2a3b4c5d6e7f",
					sizeClassKey: "10_20",
					lower: 10,
					upper: 20,
					label: "10-20 \u00b5m",
					maxCount: 1000,
					material: Material.METAL,
					useTotal: true,
					wayOfMeasurement: WayOfMeasurement.FeretMax,
				},
			],
			categoryLimits: [
				{
					id: "cat-beta-1",
					specificationId: "c3d4e5f6-a7b8-4c9d-0e1f-2a3b4c5d6e7f",
					category: "metal",
					maxSizeUm: 150,
					wayOfMeasurement: WayOfMeasurement.FeretMax,
				},
			],
		},
		{
			id: "b8c9d0e1-f2a3-4b4c-5d6e-7f8a9b0c1d2e",
			siteId: SITE_1,
			name: "Demo Spec Gamma (Site 1, inactive)",
			partNumber: "ART-003",
			partId: PART_3,
			surfaceAreaCm2: 180,
			numberOfParts: null,
			pdfBlobPath: null,
			active: false,
			sizeClassLimitNormalisation: SizeClassLimitNormalisation.Total,
			createdAt: new Date("2026-03-15T10:30:00Z"),
			updatedAt: new Date("2026-03-15T10:30:00Z"),
			extractionSteps: [],
			sizeClassLimits: [],
			categoryLimits: [
				{
					id: "cat-gamma-1",
					specificationId: "b8c9d0e1-f2a3-4b4c-5d6e-7f8a9b0c1d2e",
					category: "fiber",
					maxSizeUm: 800,
					wayOfMeasurement: WayOfMeasurement.FeretMax,
				},
			],
		},
		{
			id: "d0e1f2a3-b4c5-4d6e-7f8a-9b0c1d2e3f4a",
			siteId: SITE_2,
			name: "Demo Spec Delta (Site 2)",
			partNumber: "ART-004",
			partId: PART_4,
			surfaceAreaCm2: 420,
			numberOfParts: 10,
			pdfBlobPath: null,
			active: true,
			sizeClassLimitNormalisation: SizeClassLimitNormalisation.PerPart,
			createdAt: new Date("2026-04-05T14:00:00Z"),
			updatedAt: new Date("2026-04-05T14:00:00Z"),
			extractionSteps: [],
			sizeClassLimits: [
				{
					id: "delta-scl-1",
					specificationId: "d0e1f2a3-b4c5-4d6e-7f8a-9b0c1d2e3f4a",
					sizeClassKey: "50_100",
					lower: 50,
					upper: 100,
					label: "50-100 \u00b5m",
					maxCount: 250,
					material: Material.NON_METAL,
					useTotal: false,
					wayOfMeasurement: WayOfMeasurement.FeretMax,
				},
			],
			categoryLimits: [
				{
					id: "cat-delta-1",
					specificationId: "d0e1f2a3-b4c5-4d6e-7f8a-9b0c1d2e3f4a",
					category: "metal",
					maxSizeUm: 300,
					wayOfMeasurement: WayOfMeasurement.FeretMax,
				},
				{
					id: "cat-delta-2",
					specificationId: "d0e1f2a3-b4c5-4d6e-7f8a-9b0c1d2e3f4a",
					category: "non_metal",
					maxSizeUm: 600,
					wayOfMeasurement: WayOfMeasurement.FeretMax,
				},
			],
		},
	];

	getSpecifications(siteId?: string, active?: boolean): Observable<Specification[]> {
		let filtered = siteId ? this.specifications.filter((s) => s.siteId === siteId) : this.specifications;
		if (active !== undefined) filtered = filtered.filter((s) => s.active === active);
		return of(filtered.map((s) => ({ ...s }))).pipe(delay(this.latencyMs));
	}

	getSpecification(id: string): Observable<Specification> {
		const spec = this.specifications.find((s) => s.id === id);
		if (!spec) return throwError(() => new Error(`Specification with ID ${id} not found`));
		return of({ ...spec }).pipe(delay(this.latencyMs));
	}

	createSpecification(dto: CreateSpecificationDto): Observable<Specification> {
		const now = new Date();
		const newSpec: Specification = {
			id: crypto.randomUUID(),
			siteId: dto.siteId ?? null,
			name: dto.name,
			partNumber: dto.partNumber ?? null,
			partId: dto.partId ?? null,
			surfaceAreaCm2: dto.surfaceAreaCm2 ?? null,
			numberOfParts: dto.numberOfParts ?? null,
			pdfBlobPath: dto.pdfBlobPath ?? null,
			active: dto.active ?? true,
			sizeClassLimitNormalisation: dto.sizeClassLimitNormalisation,
			createdAt: now,
			updatedAt: now,
			extractionSteps: [],
			sizeClassLimits: (dto.sizeClassLimits ?? []).map((l) => ({
				id: "",
				specificationId: "",
				sizeClassKey: l.sizeClassKey,
				lower: l.lower,
				upper: l.upper ?? null,
				label: l.label,
				maxCount: l.maxCount ?? null,
				material: l.material ?? Material.METAL,
				useTotal: l.useTotal ?? true,
				wayOfMeasurement: l.wayOfMeasurement ?? WayOfMeasurement.FeretMax,
			})),
			categoryLimits: (dto.categoryLimits ?? []).map((c) => ({
				id: "",
				specificationId: "",
				category: c.category,
				maxSizeUm: c.maxSizeUm,
				wayOfMeasurement: c.wayOfMeasurement,
			})),
		};
		this.specifications.push(newSpec);
		return of({ ...newSpec }).pipe(delay(this.latencyMs));
	}

	updateSpecification(id: string, dto: UpdateSpecificationDto): Observable<Specification> {
		const idx = this.specifications.findIndex((s) => s.id === id);
		if (idx === -1) return throwError(() => new Error(`Specification with ID ${id} not found`));
		const current = this.specifications[idx];
		const updated: Specification = {
			...current,
			name: dto.name ?? current.name,
			partNumber: dto.partNumber !== undefined ? dto.partNumber : current.partNumber,
			partId: dto.partId !== undefined ? dto.partId : current.partId,
			surfaceAreaCm2: dto.surfaceAreaCm2 !== undefined ? dto.surfaceAreaCm2 : current.surfaceAreaCm2,
			numberOfParts: dto.numberOfParts !== undefined ? dto.numberOfParts : current.numberOfParts,
			pdfBlobPath: dto.pdfBlobPath !== undefined ? dto.pdfBlobPath : current.pdfBlobPath,
			active: dto.active !== undefined ? dto.active : current.active,
			updatedAt: new Date(),
			sizeClassLimits: dto.sizeClassLimits
				? dto.sizeClassLimits.map((l) => ({
						id: l.id || "",
						specificationId: l.specificationId || "",
						sizeClassKey: l.sizeClassKey,
						lower: l.lower,
						upper: l.upper ?? null,
						label: l.label,
						maxCount: l.maxCount ?? null,
						material: l.material ?? Material.METAL,
						useTotal: l.useTotal ?? true,
						wayOfMeasurement: l.wayOfMeasurement ?? WayOfMeasurement.FeretMax,
					}))
				: current.sizeClassLimits,
			categoryLimits: dto.categoryLimits
				? dto.categoryLimits.map((c) => ({
						id: c.id || "",
						specificationId: c.specificationId || "",
						category: c.category,
						maxSizeUm: c.maxSizeUm,
						wayOfMeasurement: c.wayOfMeasurement,
					}))
				: current.categoryLimits,
		};
		this.specifications[idx] = updated;
		return of({ ...updated }).pipe(delay(this.latencyMs));
	}

	deleteSpecification(id: string): Observable<void> {
		const idx = this.specifications.findIndex((s) => s.id === id);
		if (idx === -1) return throwError(() => new Error(`Specification with ID ${id} not found`));
		this.specifications.splice(idx, 1);
		return of(undefined).pipe(delay(this.latencyMs));
	}
}
